/**
 * UgcStudioBuilder — main UGC creator view.
 * Layout per design: left sidebar (Product/App toggle), big prompt textarea,
 * chip rows (format, aspect, quality, duration), product+avatar+generate buttons.
 * Polls jobs after enqueue and renders completed videos.
 */
import { useEffect, useMemo, useState } from "react";
import { UgcLightCtx, useUgcLight } from "./ugc-theme";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Package, Smartphone, User, Wand2, X } from "lucide-react";
import {
  ugcStudioApi,
  type AspectRatio,
  type AudioMode,
  type Duration,
  type Platform,
  type ProductType,
  type Resolution,
  type Script,
  type UgcAvatar,
  type UgcFormatId,
  type UgcProduct,
  type VideoModel,
} from "./api";
import { AudioStudioPanel } from "./audio-studio-panel";
import { FormatPickerModal } from "./format-picker-modal";
import { AvatarPickerModal } from "./avatar-picker-modal";
import { ProductPickerModal } from "./product-picker-modal";
import { ChipGroup, ChipRow, RailButton } from "./ugc-studio-chips";
import { JobCard, JobList } from "./ugc-studio-jobs";

const ASPECT_RATIOS: AspectRatio[] = ["9:16", "1:1", "16:9", "3:4", "4:5"];
const RESOLUTIONS: Resolution[] = ["480p", "720p", "1080p"];
const DURATIONS: Duration[] = [5, 10, 15];
const VIDEO_MODEL: VideoModel = "seedance-2.0";

/**
 * Per-generation device override for app integration. `auto` means: use the
 * platform stored on the picked product (default fallback: laptop).
 */
type DeviceMode = "auto" | "desktop" | "mobile";
const DEVICE_MODES: DeviceMode[] = ["auto", "desktop", "mobile"];
const DEVICE_LABEL_KEYS: Record<DeviceMode, string> = {
  auto: "ugcStudio.builder.deviceAuto",
  desktop: "ugcStudio.builder.deviceDesktop",
  mobile: "ugcStudio.builder.deviceMobile",
};

// Pricing constants come from `shared/ugc-pricing.ts` — single source of
// truth for client + server. NIE duplikuj tu USD_TO_PLN / margin / FAL.
import {
  LLM_OVERHEAD_TOKENS,
  computeAudioPipelineUsd,
  seedanceUsd,
  sumBreakdown,
  usdToTokens,
  type UgcJobCostBreakdown,
} from "@shared/ugc-pricing";

export function UgcStudioBuilder({ isLightTheme = false }: { isLightTheme?: boolean }) {
  const L = isLightTheme;
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const searchParams = new URLSearchParams(window.location.search);
  const preloadAvatarId = searchParams.get("avatarId");
  const preloadProductId = searchParams.get("productId");

  const [productType, setProductType] = useState<ProductType>("product");
  const [prompt, setPrompt] = useState(searchParams.get("prompt") ?? "");
  const [format, setFormat] = useState<UgcFormatId>(
    (searchParams.get("format") as UgcFormatId) ?? "ugc"
  );
  const [aspect, setAspect] = useState<AspectRatio>(
    (searchParams.get("aspect") as AspectRatio) ?? "9:16"
  );
  const [resolution, setResolution] = useState<Resolution>(
    (searchParams.get("resolution") as Resolution) ?? "720p"
  );
  const [duration, setDuration] = useState<Duration>(
    (Number(searchParams.get("duration")) as Duration) || 15
  );
  const [avatar, setAvatar] = useState<UgcAvatar | null>(null);
  const [product, setProduct] = useState<UgcProduct | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [audioMode, setAudioMode] = useState<AudioMode>(
    (searchParams.get("audioMode") as AudioMode) ?? "overlay"
  );
  const [script, setScript] = useState<Script>([]);
  const [voiceId, setVoiceId] = useState<string | null>(searchParams.get("voiceId"));
  const [deviceMode, setDeviceMode] = useState<DeviceMode>("auto");

  const [formatModalOpen, setFormatModalOpen] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);

  const { data: formatsData } = useQuery({
    queryKey: ["ugc-studio", "formats"],
    queryFn: () => ugcStudioApi.listFormats(),
  });

  const { data: jobsData } = useQuery({
    queryKey: ["ugc-studio", "jobs"],
    queryFn: () => ugcStudioApi.listJobs(),
    refetchInterval: 5000,
  });

  const { data: activeJobData } = useQuery({
    queryKey: ["ugc-studio", "job", activeJobId],
    queryFn: () => ugcStudioApi.getJob(activeJobId as string),
    enabled: !!activeJobId,
    refetchInterval: (q) => {
      const s = q.state.data?.job?.status;
      return s === "completed" || s === "failed" ? false : 2500;
    },
  });

  const activeJob = activeJobData?.job;

  // Auto-select avatar/product when coming from Recreate (URL params)
  const { data: preloadAvatarsData } = useQuery({
    queryKey: ["ugc-studio", "avatars", "preload", preloadAvatarId],
    queryFn: () => ugcStudioApi.listAvatars("all"),
    enabled: !!preloadAvatarId && !avatar,
  });
  useEffect(() => {
    if (!preloadAvatarId || avatar || !preloadAvatarsData?.avatars) return;
    const found = preloadAvatarsData.avatars.find((a) => a.id === preloadAvatarId);
    if (found) setAvatar(found);
  }, [preloadAvatarId, preloadAvatarsData, avatar]);

  const { data: preloadProductsData } = useQuery({
    queryKey: ["ugc-studio", "products", "preload", preloadProductId],
    queryFn: () => ugcStudioApi.listProducts(),
    enabled: !!preloadProductId && !product,
  });
  useEffect(() => {
    if (!preloadProductId || product || !preloadProductsData?.products) return;
    const found = preloadProductsData.products.find((p) => p.id === preloadProductId);
    if (found) setProduct(found);
  }, [preloadProductId, preloadProductsData, product]);

  const cleanScript = useMemo(() => script.filter((b) => b.text.trim().length > 0), [script]);
  // Pusty skrypt przy lipsync/overlay/voiceover jest OK — backend wygeneruje go
  // automatycznie z głównego promptu (ScriptGeneratorService). Pokazujemy tylko
  // miękki hint, żeby user wiedział co się stanie.
  const willAutoScript = audioMode !== "native" && cleanScript.length === 0;

  const createJobMut = useMutation({
    mutationFn: () => {
      const useElevenLabs = audioMode !== "native";
      const hasManualScript = useElevenLabs && cleanScript.length > 0;
      return ugcStudioApi.createJob({
        prompt,
        format,
        aspectRatio: aspect,
        resolution,
        durationSeconds: duration,
        videoModel: VIDEO_MODEL,
        avatarId: avatar?.id ?? null,
        productId: product?.id ?? null,
        ...(productType === "app" && deviceMode !== "auto"
          ? { deviceOverride: deviceMode as Platform }
          : {}),
        ...(useElevenLabs
          ? {
              audioMode,
              ...(hasManualScript ? { script: cleanScript } : {}),
              ...(voiceId ? { voiceId } : {}),
            }
          : {}),
      });
    },
    onSuccess: (res) => {
      setActiveJobId(res.job.id);
      qc.invalidateQueries({ queryKey: ["ugc-studio", "jobs"] });
      toast({
        title: t("ugcStudio.builder.generationStarted"),
        description: t("ugcStudio.builder.generationStartedDesc", {
          tokens: res.job.tokensCharged,
        }),
      });
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.builder.generationFailed"),
        description: err.message ?? t("ugcStudio.builder.tryAgain"),
        variant: "destructive",
      }),
  });

  const selectedFormat = useMemo(
    () => formatsData?.formats.find((f) => f.id === format),
    [formatsData, format]
  );

  const videoCost = useMemo<{ totalTokens: number; breakdown: UgcJobCostBreakdown }>(() => {
    // Mirror serwerowego `computeCostBreakdown`: video + audio pipeline + overhead.
    const charCount = cleanScript.reduce((sum, b) => sum + b.text.length, 0);
    const whisperNeeded = audioMode !== "native" && charCount === 0;
    const audio = computeAudioPipelineUsd({
      audioMode,
      durationSeconds: duration,
      scriptCharCount: charCount > 0 ? charCount : null,
      whisperNeeded,
    });
    const breakdown: UgcJobCostBreakdown = {
      videoTokens: usdToTokens(seedanceUsd(resolution, duration)),
      ttsTokens: usdToTokens(audio.ttsUsd),
      lipsyncTokens: usdToTokens(audio.lipsyncUsd),
      whisperTokens: usdToTokens(audio.whisperUsd),
      scriptLlmTokens: usdToTokens(audio.scriptLlmUsd),
      llmOverheadTokens: LLM_OVERHEAD_TOKENS,
    };
    return { totalTokens: sumBreakdown(breakdown), breakdown };
  }, [audioMode, cleanScript, duration, resolution]);

  const canGenerate = !!prompt.trim() && !createJobMut.isPending;
  const audioHint = willAutoScript ? t("ugcStudio.builder.autoScriptHint", { audioMode }) : null;

  // App promo videos only use UGC format (creator points at the device showing the app).
  useEffect(() => {
    if (productType === "app" && format !== "ugc") setFormat("ugc");
  }, [productType, format]);

  const availableFormats = useMemo(() => {
    const all = formatsData?.formats ?? [];
    return productType === "app" ? all.filter((f) => f.id === "ugc") : all;
  }, [formatsData, productType]);

  return (
    <UgcLightCtx.Provider value={L}>
      <div
        className={`flex flex-col h-full overflow-hidden relative ${L ? "text-gray-900" : "text-white"}`}
      >
        {/* Ambient brand glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-32 -left-24 w-[420px] h-[420px] bg-[#ef3e29]/10 rounded-full blur-[140px]" />
          <div className="absolute -bottom-24 right-0 w-[380px] h-[380px] bg-rose-500/[0.06] rounded-full blur-[140px]" />
        </div>

        {/* Header */}
        <div className="relative px-4 md:px-8 pt-8 pb-5">
          <div className="max-w-5xl mx-auto">
            <h1
              className={`text-xl md:text-2xl font-semibold tracking-tight bg-clip-text text-transparent bg-gradient-to-r ${L ? "from-gray-900 to-gray-500" : "from-white/95 to-white/55"}`}
            >
              {t("ugcStudio.builder.title")}
            </h1>
            <p className={`mt-1 text-sm ${L ? "text-gray-500" : "text-white/45"}`}>
              {t("ugcStudio.builder.subtitle")}
            </p>
          </div>
        </div>

        {/* Main card */}
        <div className="relative flex-1 min-h-0 flex flex-col px-4 md:px-8 pb-6 gap-4 overflow-y-auto">
          <div
            className={`max-w-5xl w-full mx-auto rounded-2xl backdrop-blur-2xl border shadow-2xl p-5 md:p-7 flex flex-col ${L ? "bg-white border-gray-200" : "bg-white/[0.02] border-white/[0.05]"}`}
          >
            <div className="flex gap-6">
              {/* Left rail: type switch */}
              <aside className="flex flex-col gap-2 w-40 flex-shrink-0">
                <div
                  className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 px-1 ${L ? "text-gray-400" : "text-white/30"}`}
                >
                  {t("ugcStudio.builder.typeLabel")}
                </div>
                <RailButton
                  label={t("ugcStudio.builder.productType")}
                  icon={<Package className="w-4 h-4" />}
                  active={productType === "product"}
                  onClick={() => {
                    setProductType("product");
                    setProduct(null);
                  }}
                />
                <RailButton
                  label={t("ugcStudio.builder.appType")}
                  icon={<Smartphone className="w-4 h-4" />}
                  active={productType === "app"}
                  onClick={() => {
                    setProductType("app");
                    setProduct(null);
                  }}
                />
              </aside>

              {/* Center prompt + chips */}
              <div className="flex-1 min-w-0 flex flex-col gap-5">
                <div className="relative group">
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={
                      selectedFormat?.userBriefHint ?? t("ugcStudio.builder.promptPlaceholder")
                    }
                    className={`min-h-[160px] text-base resize-none rounded-2xl px-5 py-4 focus-visible:ring-2 focus-visible:ring-[#ef3e29]/40 focus-visible:border-[#ef3e29]/40 transition ${L ? "bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400" : "bg-black/30 border-white/[0.06] text-white placeholder:text-white/30"}`}
                  />
                  <div
                    className={`absolute bottom-3 right-3 text-[10px] tabular-nums ${L ? "text-gray-400" : "text-white/25"}`}
                  >
                    {prompt.length} / 4000
                  </div>
                </div>

                {/* Chip rows */}
                <div className="space-y-3">
                  <ChipRow
                    label="Format"
                    value={selectedFormat?.label ?? format}
                    onClick={() => setFormatModalOpen(true)}
                    accent
                  />
                  <ChipGroup
                    label={t("ugcStudio.builder.aspectRatioLabel")}
                    options={ASPECT_RATIOS}
                    value={aspect}
                    onChange={setAspect}
                  />
                  <ChipGroup
                    label={t("ugcStudio.builder.qualityLabel")}
                    options={RESOLUTIONS}
                    value={resolution}
                    onChange={setResolution}
                  />
                  <ChipGroup
                    label={t("ugcStudio.builder.durationLabel")}
                    options={DURATIONS}
                    value={duration}
                    onChange={setDuration}
                    renderLabel={(d) => `${d}s`}
                  />
                  {productType === "app" ? (
                    <ChipGroup
                      label={t("ugcStudio.builder.deviceLabel")}
                      options={DEVICE_MODES}
                      value={deviceMode}
                      onChange={setDeviceMode}
                      renderLabel={(d) => t(DEVICE_LABEL_KEYS[d])}
                    />
                  ) : null}
                </div>

                {/* Asset pickers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  <AssetTile
                    icon={
                      productType === "app" ? (
                        <Smartphone className="w-4 h-4" />
                      ) : (
                        <Package className="w-4 h-4" />
                      )
                    }
                    label={
                      productType === "app"
                        ? t("ugcStudio.builder.appType")
                        : t("ugcStudio.builder.productType")
                    }
                    value={product?.name ?? null}
                    thumbnail={product?.imageUrls?.[0] ?? null}
                    onClick={() => setProductModalOpen(true)}
                    onClear={product ? () => setProduct(null) : undefined}
                  />
                  <AssetTile
                    icon={<User className="w-4 h-4" />}
                    label="Avatar"
                    value={avatar?.name ?? null}
                    thumbnail={avatar?.signedUrl ?? null}
                    onClick={() => setAvatarModalOpen(true)}
                    onClear={avatar ? () => setAvatar(null) : undefined}
                  />
                </div>

                <AudioStudioPanel
                  format={format}
                  durationSeconds={duration}
                  avatarId={avatar?.id ?? null}
                  productId={product?.id ?? null}
                  userBrief={prompt}
                  disabled={createJobMut.isPending}
                  audioMode={audioMode}
                  script={script}
                  onAudioModeChange={setAudioMode}
                  onScriptChange={setScript}
                  voiceId={voiceId}
                  onVoiceChange={setVoiceId}
                />

                {/* Inline action footer — cost estimate + generate */}
                <div
                  className={`flex items-center gap-3 flex-wrap pt-3 border-t ${L ? "border-gray-200" : "border-white/[0.05]"}`}
                >
                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${L ? "bg-gray-100 border-gray-200" : "bg-white/[0.04] border-white/[0.08]"}`}
                    title={`Video ${videoCost.breakdown.videoTokens} • TTS ${videoCost.breakdown.ttsTokens} • Lipsync ${videoCost.breakdown.lipsyncTokens} • Whisper ${videoCost.breakdown.whisperTokens} • LLM ${videoCost.breakdown.scriptLlmTokens + videoCost.breakdown.llmOverheadTokens}`}
                  >
                    <div className="w-2 h-2 rounded-full bg-[#ef3e29] shadow-[0_0_8px_rgba(239,62,41,0.6)]" />
                    <span className={`text-[11px] ${L ? "text-gray-500" : "text-white/50"}`}>
                      {t("ugcStudio.builder.costLabel")}
                    </span>
                    <span
                      className={`font-semibold text-sm tabular-nums ${L ? "text-gray-900" : "text-white"}`}
                    >
                      {videoCost.totalTokens.toLocaleString("pl-PL")}
                    </span>
                    <span className={`text-[11px] ${L ? "text-gray-400" : "text-white/40"}`}>
                      {t("ugcStudio.builder.costUnit")}
                    </span>
                  </div>
                  <div className="flex-1" />
                  {audioHint && (
                    <span
                      className={`text-[11px] max-w-[280px] leading-snug ${L ? "text-gray-500" : "text-white/55"}`}
                    >
                      {audioHint}
                    </span>
                  )}
                  <Button
                    onClick={() => createJobMut.mutate()}
                    disabled={!canGenerate}
                    className="h-10 px-5 bg-[#ef3e29] hover:bg-[#d42a1a] text-white font-semibold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_24px_rgba(239,62,41,0.25)]"
                  >
                    {createJobMut.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Wand2 className="w-4 h-4 mr-2" />
                    )}
                    {t("ugcStudio.builder.generateButton")}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Active job + history */}
          {activeJob && <JobCard job={activeJob} highlighted />}
          <JobList
            jobs={jobsData?.jobs ?? []}
            activeId={activeJobId}
            onOpen={(id) => setActiveJobId(id)}
          />
        </div>

        {/* Modals */}
        <FormatPickerModal
          open={formatModalOpen}
          onClose={() => setFormatModalOpen(false)}
          formats={availableFormats}
          selectedId={format}
          onSelect={(id) => {
            setFormat(id);
            const fmt = formatsData?.formats.find((f) => f.id === id);
            if (fmt) {
              setAspect(fmt.defaultAspectRatio);
              setDuration(fmt.defaultDuration);
            }
          }}
        />
        <AvatarPickerModal
          open={avatarModalOpen}
          onClose={() => setAvatarModalOpen(false)}
          selectedId={avatar?.id ?? null}
          onSelect={setAvatar}
        />
        <ProductPickerModal
          open={productModalOpen}
          onClose={() => setProductModalOpen(false)}
          type={productType}
          selectedId={product?.id ?? null}
          onSelect={setProduct}
        />
      </div>
    </UgcLightCtx.Provider>
  );
}

interface AssetTileProps {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  thumbnail: string | null;
  onClick: () => void;
  onClear?: () => void;
}

function AssetTile({ icon, label, value, thumbnail, onClick, onClear }: AssetTileProps) {
  const L = useUgcLight();
  const { t } = useTranslation();
  const selected = !!value;
  return (
    <div
      onClick={onClick}
      className={`group relative flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-all ${
        selected
          ? L
            ? "bg-gray-50 border-gray-300 hover:border-gray-400"
            : "bg-gradient-to-br from-white/[0.06] to-white/[0.02] border-white/20 hover:border-white/30"
          : L
            ? "bg-gray-50/50 border-gray-200 border-dashed hover:border-gray-300 hover:bg-gray-100"
            : "bg-white/[0.02] border-white/[0.08] border-dashed hover:border-white/20 hover:bg-white/[0.04]"
      }`}
    >
      <div
        className={`flex items-center justify-center w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 ${
          thumbnail
            ? "bg-black/40"
            : L
              ? "bg-gray-100 text-gray-400"
              : "bg-white/[0.04] text-white/50"
        }`}
      >
        {thumbnail ? (
          <img src={thumbnail} alt={value ?? ""} className="w-full h-full object-cover" />
        ) : (
          icon
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className={`text-[10px] uppercase tracking-[0.15em] font-semibold ${L ? "text-gray-400" : "text-white/40"}`}
        >
          {label}
        </div>
        <div
          className={`text-sm font-medium truncate ${selected ? (L ? "text-gray-900" : "text-white") : L ? "text-gray-400" : "text-white/50"}`}
        >
          {value ?? t("ugcStudio.builder.selectPlaceholder", { label: label.toLowerCase() })}
        </div>
      </div>
      {onClear && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className={`opacity-0 group-hover:opacity-100 p-1.5 rounded-full transition ${L ? "hover:bg-gray-200 text-gray-500" : "hover:bg-white/10 text-white/60"}`}
          aria-label={t("ugcStudio.builder.removeLabel", { label })}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
